import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { logActivity } from '../lib/activity';
import { useProfile } from '../hooks/useProfile';

const C = {
  navy:    '#0A1628', gold:    '#C9A84C', black:   '#0A0A0A',
  white:   '#FFFFFF', surface: '#F5F6F8', border:  '#D0D5DD',
  borderLt:'#E8EBF0', text:    '#0A0A0A', textSec: '#3D4552',
  muted:   '#6B7280', mutedLt: '#9CA3AF',
  success: '#0A7C42', teal:    '#0D9488',
};

interface Fund {
  id: string; name: string; asset_class: string; strategy: string;
  aum_min: number; aum_max: number; management_fee: number; performance_fee: number;
  minimum_subscription: number; regional_focus: string; vehicle_type: string;
  open_to_sma: boolean; firm_name: string; headquarters: string;
}
interface Message { role: 'user' | 'assistant'; content: string; }

const SUGGESTED_PROMPTS = [
  'How do my fees compare to peers in my strategy?',
  'What is the average management fee for hedge funds?',
  'How competitive is my minimum subscription?',
  'What AUM range is typical for quant funds?',
  'How does my fund compare to others in the market?',
  'What are common performance fee structures?',
  'What strategies are most represented on the platform?',
  "How does my fund's structure compare to peers?",
];

function formatFundsContext(funds: Fund[]): string {
  return funds.map(f => `
Fund: ${f.name}
Firm: ${f.firm_name}
Headquarters: ${f.headquarters}
Asset Class: ${f.asset_class.replace('_', ' ')}
Strategy: ${f.strategy}
AUM: $${f.aum_min}mm – $${f.aum_max}mm
Management Fee: ${f.management_fee}%
Performance Fee: ${f.performance_fee}%
Minimum Subscription: $${f.minimum_subscription}mm
Regional Focus: ${f.regional_focus}
Vehicle Type: ${f.vehicle_type}
Open to SMAs: ${f.open_to_sma ? 'Yes' : 'No'}
  `.trim()).join('\n\n---\n\n');
}

export default function MarketIntelligence() {
  const navigate = useNavigate();
  const { firm } = useProfile();
  const [funds, setFunds]             = useState<Fund[]>([]);
  const [messages, setMessages]       = useState<Message[]>([]);
  const [input, setInput]             = useState('');
  const [loading, setLoading]         = useState(false);
  const [fundsLoaded, setFundsLoaded] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { fetchFunds(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const fetchFunds = async () => {
    const { data } = await supabase.from('funds')
      .select(`id, name, asset_class, strategy, aum_min, aum_max,
        management_fee, performance_fee, minimum_subscription,
        regional_focus, vehicle_type, open_to_sma,
        firm:firms(name, headquarters)`)
      .eq('status', 'open');
    if (data) {
      setFunds(data.map((f: any) => ({
        ...f,
        firm_name:    Array.isArray(f.firm) ? f.firm[0]?.name         : f.firm?.name,
        headquarters: Array.isArray(f.firm) ? f.firm[0]?.headquarters : f.firm?.headquarters,
      })));
    }
    setFundsLoaded(true);
  };

  const sendMessage = async (text: string) => {
    if (!text.trim() || loading) return;
    const userMessage: Message = { role: 'user', content: text.trim() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    const systemPrompt = `You are Altveno AI, an intelligent market intelligence assistant for alternative investment fund managers. Your job is to help managers understand how their fund compares to the market, anonymously and in aggregate.

Here are all funds currently on the platform (all data is anonymous in your responses, never reveal which specific fund has which fee unless it is the manager's own fund):

${formatFundsContext(funds)}

Guidelines:
- Help managers benchmark their fees, terms, AUM, and strategy against anonymous peer data.
- Never reveal the name of a specific competitor fund when discussing benchmarks, always aggregate.
- Be concise, data-driven, and professional.
- Use ranges and averages when comparing (e.g. "Management fees range from X% to Y%, with an average of Z%").
- If asked about performance data, explain that live returns are available in the Peer Analysis section.
- Keep responses focused and scannable, use short paragraphs or bullet points.
${firm ? `\nThe manager asking is from: ${firm.name}` : ''}`;

    try {
      const { data, error } = await supabase.functions.invoke('altveno-ai', {
        body: { systemPrompt, messages: newMessages.map(m => ({ role: m.role, content: m.content })) },
      });
      if (error) throw error;
      const content = data.content?.[0]?.text || 'Sorry, I could not generate a response.';
      setMessages(prev => [...prev, { role: 'assistant', content }]);
      const uid = (await supabase.auth.getUser()).data.user?.id;
      if (uid) logActivity(uid, firm?.id ?? null, 'ai_query_made', 'ai', 'fund_discovery', { query_length: text.length });
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' }]);
    }
    setLoading(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  const isEmpty = messages.length === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: C.surface, fontFamily: 'system-ui, -apple-system, sans-serif' }}>

      {/* Header */}
      <div style={{ background: C.white, borderBottom: `1px solid ${C.border}`, padding: '14px 28px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '32px', height: '32px', borderRadius: '3px', background: C.navy, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.gold} strokeWidth="2" strokeLinecap="round">
              <path d="M9.663 17h4.673M12 3v1m6.364 1.636-.707.707M21 12h-1M4 12H3m1.636-6.364.707.707M12 21v-1M7 12a5 5 0 1 1 10 0c0 1.887-1.009 3.54-2.5 4.395V18a1 1 0 0 1-1 1h-3a1 1 0 0 1-1-1v-1.605C8.009 15.54 7 13.887 7 12z"/>
            </svg>
          </div>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: C.navy, fontFamily: 'Georgia, serif' }}>
              Altveno AI, Market Intelligence
            </div>
            <div style={{ fontSize: '11px', color: C.muted, marginTop: '1px' }}>
              {fundsLoaded ? `${funds.length} fund${funds.length !== 1 ? 's' : ''} in benchmark dataset · Anonymous aggregated data` : 'Loading benchmark data...'}
            </div>
          </div>
          <div style={{ marginLeft: 'auto', fontSize: '10px', color: C.mutedLt, fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
            For Managers
          </div>
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>
        {isEmpty ? (
          <div style={{ maxWidth: '680px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '36px', paddingTop: '20px' }}>
              <div style={{ width: '48px', height: '48px', borderRadius: '4px', background: C.navy, margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.gold} strokeWidth="1.8" strokeLinecap="round">
                  <path d="M9.663 17h4.673M12 3v1m6.364 1.636-.707.707M21 12h-1M4 12H3m1.636-6.364.707.707M12 21v-1M7 12a5 5 0 1 1 10 0c0 1.887-1.009 3.54-2.5 4.395V18a1 1 0 0 1-1 1h-3a1 1 0 0 1-1-1v-1.605C8.009 15.54 7 13.887 7 12z"/>
                </svg>
              </div>
              <h2 style={{ fontFamily: 'Georgia, serif', fontSize: '22px', color: C.black, fontWeight: 400, marginBottom: '8px' }}>
                Market Intelligence
              </h2>
              <p style={{ fontSize: '13px', color: C.muted, lineHeight: 1.7, maxWidth: '420px', margin: '0 auto' }}>
                Ask me how your fund compares to peers, fees, terms, AUM, and strategy. All benchmark data is anonymous and aggregated.
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {SUGGESTED_PROMPTS.map((prompt, i) => (
                <button key={i} onClick={() => sendMessage(prompt)} style={{
                  background: C.white, border: `1px solid ${C.border}`, borderRadius: '3px',
                  padding: '12px 14px', textAlign: 'left', cursor: 'pointer',
                  fontSize: '12.5px', color: C.textSec, fontFamily: 'system-ui', lineHeight: 1.4, transition: 'all 0.1s',
                }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = C.navy; (e.currentTarget as HTMLButtonElement).style.color = C.navy; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = C.border; (e.currentTarget as HTMLButtonElement).style.color = C.textSec; }}
                >{prompt}</button>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ maxWidth: '680px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {messages.map((msg, i) => (
              <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                {msg.role === 'assistant' ? (
                  <div style={{ width: '26px', height: '26px', borderRadius: '3px', background: C.navy, flexShrink: 0, marginTop: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={C.gold} strokeWidth="2" strokeLinecap="round">
                      <path d="M9.663 17h4.673M12 3v1m6.364 1.636-.707.707M21 12h-1M4 12H3m1.636-6.364.707.707M12 21v-1M7 12a5 5 0 1 1 10 0c0 1.887-1.009 3.54-2.5 4.395V18a1 1 0 0 1-1 1h-3a1 1 0 0 1-1-1v-1.605C8.009 15.54 7 13.887 7 12z"/>
                    </svg>
                  </div>
                ) : (
                  <div style={{ width: '26px', height: '26px', borderRadius: '3px', background: C.surface, border: `1px solid ${C.border}`, flexShrink: 0, marginTop: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 700, color: C.muted, letterSpacing: '0.3px' }}>
                    YOU
                  </div>
                )}
                <div style={{
                  flex: 1, fontSize: '13.5px', lineHeight: 1.75,
                  color: msg.role === 'assistant' ? C.text : C.muted,
                  whiteSpace: 'pre-wrap',
                  background: msg.role === 'assistant' ? C.white : 'transparent',
                  border: msg.role === 'assistant' ? `1px solid ${C.border}` : 'none',
                  borderRadius: msg.role === 'assistant' ? '3px' : '0',
                  padding: msg.role === 'assistant' ? '12px 14px' : '0',
                }}>
                  {msg.role === 'assistant'
                    ? msg.content.replace(/\*\*(.*?)\*\*/g, '$1')
                    : msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <div style={{ width: '26px', height: '26px', borderRadius: '3px', background: C.navy, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={C.gold} strokeWidth="2" strokeLinecap="round">
                    <path d="M9.663 17h4.673M12 3v1m6.364 1.636-.707.707M21 12h-1M4 12H3m1.636-6.364.707.707M12 21v-1M7 12a5 5 0 1 1 10 0c0 1.887-1.009 3.54-2.5 4.395V18a1 1 0 0 1-1 1h-3a1 1 0 0 1-1-1v-1.605C8.009 15.54 7 13.887 7 12z"/>
                  </svg>
                </div>
                <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '3px', padding: '12px 14px', display: 'flex', gap: '4px', alignItems: 'center' }}>
                  {[0, 1, 2].map(i => (
                    <div key={i} style={{ width: '5px', height: '5px', borderRadius: '50%', background: C.navy, opacity: 0.4, animation: `dot-pulse 1.2s ease-in-out ${i * 0.2}s infinite` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>
        )}
      </div>

      {/* Input */}
      <div style={{ background: C.white, borderTop: `1px solid ${C.border}`, padding: '14px 28px', flexShrink: 0 }}>
        <div style={{ maxWidth: '680px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '3px', padding: '8px 10px' }}>
            <textarea
              value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
              placeholder="Ask about fees, terms, strategy benchmarks..."
              rows={1}
              style={{ flex: 1, background: 'transparent', border: 'none', color: C.text, fontSize: '13px', outline: 'none', fontFamily: 'system-ui', resize: 'none', lineHeight: 1.5, maxHeight: '100px', overflowY: 'auto' }}
            />
            <button onClick={() => sendMessage(input)} disabled={!input.trim() || loading} style={{
              background: input.trim() && !loading ? C.navy : C.surface,
              border: `1px solid ${input.trim() && !loading ? C.navy : C.border}`,
              borderRadius: '3px', width: '30px', height: '30px', flexShrink: 0,
              cursor: input.trim() && !loading ? 'pointer' : 'not-allowed',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: input.trim() && !loading ? C.white : C.mutedLt,
              transition: 'all 0.1s', fontSize: '14px',
            }}>↑</button>
          </div>
          <div style={{ fontSize: '10px', color: C.mutedLt, textAlign: 'center', marginTop: '6px', letterSpacing: '0.3px' }}>
            Press Enter to send · Shift+Enter for new line · All benchmark data is anonymous and aggregated
          </div>
        </div>
      </div>

      <style>{`
        @keyframes dot-pulse {
          0%, 100% { opacity: 0.2; transform: scale(0.8); }
          50% { opacity: 0.8; transform: scale(1.1); }
        }
      `}</style>
    </div>
  );
}